import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { CASE_STUDIES } from "@/lib/caseStudies";

export default function CaseStudies() {
  return (
    <main data-testid="case-studies-page" className="bg-white">
      <Seo
        title="Case studies"
        description="Real campaigns for real highways and infrastructure businesses. See how HighwaysIndustry.com has helped Kier, WJ Group, Highway Care, Blakedale, R&C Williams, Eurovia and more."
        path="/case-studies"
      />
      {/* ─────────────── Page header ─────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              Case Studies
            </span>
          </div>
          <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              data-testid="case-studies-headline"
              className="lg:col-span-8 font-heading font-black text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter text-slate-900"
            >
              Recent work,
              <br />
              <span className="text-accent-blue italic">in the field.</span>
            </motion.h1>
            <p className="lg:col-span-4 text-lg text-slate-600 leading-relaxed">
              A look at how we&apos;ve helped highways businesses bring new
              products, services and stories to market.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────── Thumbnail grid ─────────────── */}
      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid md:grid-cols-2 gap-6 md:gap-8">
          {CASE_STUDIES.map((cs, i) => (
            <motion.div
              key={cs.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
            >
              <Link
                to={`/case-studies/${cs.slug}`}
                data-testid={`case-study-card-${cs.slug}`}
                className="dark-section group block relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 aspect-[4/5] md:aspect-[4/3] hover:border-accent-blue transition-colors shadow-xl"
              >
                {/* Poster image */}
                {cs.thumb ? (
                  <img
                    src={cs.thumb}
                    alt={cs.cardTitle}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-black">
                    <div className="absolute inset-0 opacity-25 blueprint-dots" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-mono text-[10px] tracking-widest text-accent-blue">
                        PHOTOGRAPHY IN PROGRESS
                      </span>
                    </div>
                  </div>
                )}
                {/* Gradient + dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-black/15 group-hover:bg-black/5 transition-colors" />

                {/* Top tag */}
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-black/70 backdrop-blur-md text-white px-3 py-1.5 text-[10px] font-mono tracking-widest rounded-full">
                  <span className="h-1.5 w-1.5 bg-accent-blue rounded-full" />
                  {cs.eyebrowLabel}
                </div>

                {/* Bottom content */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <p className="font-mono text-xs tracking-widest text-accent-blue">
                    {cs.cardTagline}
                  </p>
                  <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mt-3 text-white tracking-tighter leading-[0.95]">
                    {cs.cardTitle}
                  </h2>
                  <p className="mt-4 text-sm md:text-base text-zinc-200 leading-relaxed line-clamp-3 max-w-xl">
                    {cs.heroSub}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white group-hover:text-accent-blue group-hover:gap-3 transition-all">
                    Open case study
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─────────────── Final CTA ─────────────── */}
      <section
        data-testid="case-studies-cta"
        className="dark-section bg-zinc-950 py-24 md:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-dots opacity-30" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Talk To Highways Industry
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl mt-5 leading-[0.95] tracking-tighter text-white">
              Launching new technology
              <br />
              <span className="text-accent-blue">
                into the highways sector?
              </span>
            </h2>
            <p className="mt-8 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              From product launches and innovation trials to live project
              delivery, Highways Industry creates the video, photography and
              content needed to tell your story and reach the right audience.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="case-studies-cta-talk"
              className="group inline-flex items-center gap-3 bg-primary-blue text-white px-8 py-5 text-sm md:text-base font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
            >
              Talk To Us
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
